import { useDispatch } from 'react-redux'
import { IConfigStateGroups } from 'constants/interfaces'
import { updateGroup } from 'redux/actions/admin'
import {
  mapGroupsByUsers,
  mapUsersByGroups,
  setGroups,
  setMappedGroupIdsByUsers,
  setMappedGroupsByUsers,
  setMappedRetailUsersByGroups,
} from 'redux/actions/config'
import { useToast } from '@chakra-ui/core'

export const useGroupService = () => {
  const dispatch = useDispatch()
  const toast = useToast()

  const updateLocalGroups = (
    groups: IConfigStateGroups,
    uid: string,
    deletedGroups: string[],
    addedGroups: string[]
  ): IConfigStateGroups => {
    const updatedGroups = { ...groups }

    deletedGroups.forEach((groupId) => {
      if (updatedGroups[groupId]) {
        updatedGroups[groupId] = {
          ...updatedGroups[groupId],
          uids: updatedGroups[groupId].uids.filter((id) => id !== uid),
        }
      }
    })

    addedGroups.forEach((groupId) => {
      if (updatedGroups[groupId]) {
        updatedGroups[groupId] = {
          ...updatedGroups[groupId],
          uids: [...updatedGroups[groupId].uids, uid],
        }
      }
    })

    return updatedGroups
  }

  const updateGroupMappings = (updatedGroups: IConfigStateGroups, retailUsersObject: any) => {
    const usersByGroups = mapUsersByGroups({
      groups: updatedGroups,
      retailUsersObject: retailUsersObject || {},
    })

    const { mappedGroupsByUsers, mappedGroupIdsByUsers } = mapGroupsByUsers(updatedGroups)

    dispatch(setMappedRetailUsersByGroups(usersByGroups))
    dispatch(setMappedGroupsByUsers(mappedGroupsByUsers))
    dispatch(setMappedGroupIdsByUsers(mappedGroupIdsByUsers))
  }

  const updateUserGroups = async (
    uid: string,
    selectedIds: string[],
    initialGroups: string[],
    groups: IConfigStateGroups,
    retailUsersObject: any
  ) => {
    try {
      const added_groups_ids = selectedIds.filter((id) => !initialGroups.includes(id))
      const deleted_groups_ids = initialGroups.filter((id) => !selectedIds.includes(id))

      // Process removed groups first
      const deletePromises = deleted_groups_ids.map((groupId) =>
        dispatch(
          updateGroup({
            groupId,
            added_users_ids: [],
            deleted_users_ids: [uid],
          })
        )
      )

      await Promise.all(deletePromises)

      // Then process added groups
      const addPromises = added_groups_ids.map((groupId) =>
        dispatch(
          updateGroup({
            groupId,
            added_users_ids: [uid],
            deleted_users_ids: [],
          })
        )
      )

      await Promise.all(addPromises)

      // Update local state
      const updatedGroups = updateLocalGroups(groups, uid, deleted_groups_ids, added_groups_ids)
      dispatch(setGroups(updatedGroups))
      updateGroupMappings(updatedGroups, retailUsersObject)
      toast({
        title: 'User groups updated',
        description: 'User groups updated successfully',
        status: 'success',
        duration: 3000,
        position: 'top-right',
      })
    } catch (error) {
      console.error('Error updating user groups:', error)
      toast({
        title: 'Error updating user groups',
        description: 'Please try again',
        status: 'error',
        duration: 3000,
        position: 'top-right',
      })
    }
  }

  return {
    updateUserGroups,
  }
}
